import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";

function getOptionDivision(option: DropdownOption): string {
  const metadataDivision = option.metadata?.division;
  if (typeof metadataDivision === "string" && metadataDivision !== "") {
    return metadataDivision;
  }
  return option.value.split("_")[0] ?? "";
}

function getOptionWarehouseId(option: DropdownOption): string {
  const metadataId = option.metadata?.warehouseID;
  if (typeof metadataId === "number" || typeof metadataId === "string") {
    return String(metadataId);
  }
  return option.value.split("_").slice(1).join("_");
}

/**
 * Resolves the warehouse option a PO is currently delivering to, using the
 * live warehouse number first and falling back to a label match against the
 * row's display name (fixture rows only carry "W01 - Atlanta" style names).
 */
export function resolveCurrentDeliverTo(
  rows: BulkOrderCreationRow[],
  options: DropdownOption[]
): DropdownOption | undefined {
  for (const row of rows) {
    if (row.warehouseNumber !== undefined) {
      const byId = options.find(
        (option) =>
          getOptionWarehouseId(option) === String(row.warehouseNumber) &&
          (!row.division ||
            getOptionDivision(option).toUpperCase() ===
              row.division.toUpperCase())
      );
      if (byId) return byId;
    }
    const name = row.shipToWarehouse.trim().toLowerCase();
    if (name === "") continue;
    const byLabel = options.find((option) => {
      const label = option.label.trim().toLowerCase();
      return label !== "" && (name.includes(label) || label.includes(name));
    });
    if (byLabel) return byLabel;
  }
  return undefined;
}

/**
 * Limits Deliver To choices to warehouses in the same distribution center
 * (division) as the PO's current location. When the current location cannot
 * be resolved, all options are returned so the user is never left without a
 * choice.
 */
export function getDeliverToOptions(
  current: DropdownOption | undefined,
  options: DropdownOption[]
): DropdownOption[] {
  if (!current) return options;
  const division = getOptionDivision(current);
  return options.filter((option) => getOptionDivision(option) === division);
}
